import * as XLSX from "xlsx-js-style";

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function cleanCloneForExport(rootEl) {
  if (!rootEl) return null;
  const clone = rootEl.cloneNode(true);

  clone.querySelectorAll(".no-print, .apr-author-meta, .apr-view-toggle__toolbar, button, .ant-btn").forEach((node) => {
    node.remove();
  });

  clone.querySelectorAll("[style*='display: none'], [style*='display:none']").forEach((node) => {
    if (node instanceof HTMLElement && node.style.display === "none") {
      node.remove();
    }
  });

  return clone;
}

function nearestHeading(table) {
  let node = table.previousElementSibling;
  while (node) {
    if (/^H[1-4]$/i.test(node.tagName)) {
      return String(node.textContent || "")
        .replace(/\s+/g, " ")
        .trim();
    }
    if (node.querySelector?.("h1,h2,h3,h4")) {
      const h = node.querySelector("h1,h2,h3,h4");
      return String(h?.textContent || "")
        .replace(/\s+/g, " ")
        .trim();
    }
    node = node.previousElementSibling;
  }

  const card = table.closest(".card");
  const cardTitle = card?.querySelector("h3, h2, .card-header");
  if (cardTitle) {
    return String(cardTitle.textContent || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  return "";
}

function sanitizeSheetName(name, index) {
  const cleaned = String(name || `Table ${index + 1}`)
    .replace(/[\\/?*\[\]:]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 28);
  return cleaned || `Table ${index + 1}`;
}

function uniqueSheetName(base, used) {
  let name = base;
  let n = 2;
  while (used.has(name)) {
    const suffix = ` ${n}`;
    name = `${base.slice(0, Math.max(1, 31 - suffix.length))}${suffix}`;
    n += 1;
  }
  used.add(name);
  return name;
}

/**
 * Download APR report HTML content as a Word-compatible .doc file.
 */
export function downloadAprReportWord(rootEl, { title, filename } = {}) {
  const clone = cleanCloneForExport(rootEl);
  if (!clone) {
    throw new Error("Report content is not ready for Word download.");
  }

  const safeTitle = title || "APR Report";
  const html = `
<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:w="urn:schemas-microsoft-com:office:word"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<title>${safeTitle}</title>
<!--[if gte mso 9]>
<xml>
  <w:WordDocument>
    <w:View>Print</w:View>
    <w:Zoom>100</w:Zoom>
  </w:WordDocument>
</xml>
<![endif]-->
<style>
  body {
    font-family: "Segoe UI", Tahoma, Geneva, Verdana, Arial, sans-serif;
    font-size: 11pt;
    color: #111;
    line-height: 1.4;
  }
  h1, h2, h3, h4 {
    font-family: "Segoe UI", Tahoma, Geneva, Verdana, Arial, sans-serif;
    color: #16325a;
  }
  h2 { text-align: center; }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 12px 0 18px;
    font-size: 9pt;
  }
  th, td {
    border: 1px solid #999;
    padding: 4px 6px;
    vertical-align: top;
    word-wrap: break-word;
  }
  th {
    background: #eef2f6;
    font-weight: 600;
    color: #16325a;
  }
  .card { margin-bottom: 16px; }
  img, canvas { max-width: 100%; height: auto; }
</style>
</head>
<body>
  <h1>${safeTitle}</h1>
  ${clone.innerHTML}
</body>
</html>`;

  const blob = new Blob(["\ufeff", html], {
    type: "application/msword;charset=utf-8",
  });
  const name = (filename || safeTitle).replace(/\.docx?$/i, "");
  triggerDownload(blob, `${name}.doc`);
}

/**
 * Download all HTML tables in the APR report root as an Excel workbook.
 */
export function downloadAprReportExcel(rootEl, { title, filename } = {}) {
  const clone = cleanCloneForExport(rootEl);
  if (!clone) {
    throw new Error("Report content is not ready for Excel download.");
  }

  const tables = Array.from(clone.querySelectorAll("table"));
  if (tables.length === 0) {
    throw new Error("No tables found in the report to export.");
  }

  const wb = XLSX.utils.book_new();
  const usedNames = new Set();

  tables.forEach((table, index) => {
    const heading = nearestHeading(table);
    const sheetName = uniqueSheetName(sanitizeSheetName(heading, index), usedNames);
    const ws = XLSX.utils.table_to_sheet(table, { raw: true });

    // Light header styling when possible
    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let C = range.s.c; C <= range.e.c; C += 1) {
      const addr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[addr]) continue;
      ws[addr].s = {
        font: { bold: true, color: { rgb: "16325A" } },
        fill: { patternType: "solid", fgColor: { rgb: "EEF2F6" } },
        alignment: { wrapText: true, vertical: "top" },
      };
    }

    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  // Cover / summary sheet first if title provided
  if (title) {
    const summary = XLSX.utils.aoa_to_sheet([
      ["APR Report Export"],
      ["Title", title],
      ["Tables exported", tables.length],
      ["Generated", new Date().toLocaleString()],
    ]);
    XLSX.utils.book_append_sheet(wb, summary, "Summary");
    // Move Summary to first position
    const order = wb.SheetNames.filter((n) => n !== "Summary");
    wb.SheetNames = ["Summary", ...order];
  }

  const name = (filename || title || "APR_Report").replace(/\.xlsx$/i, "");
  XLSX.writeFile(wb, `${name}.xlsx`);
}
